export const productCatalog = {
  pt: [
    {
      slug: 'context-hub',
      name: 'Dooloop Context Hub',
      kicker: 'Descoberta de contexto operacional',
      headline: 'Encontre ownership, dependências e contexto real sem depender de memória ou busca solta.',
      summary:
        'O Context Hub consolida documentação, repositórios, ambientes, tickets, incidentes e metadados técnicos em uma camada viva de contexto com busca, relações e respostas apoiadas por fonte.',
      description:
        'Na prática, ele vira o lugar onde plataforma, SRE, suporte e operações entram quando precisam entender rapidamente o que existe em um ambiente, quem responde por cada parte, o que mudou recentemente e onde está a informação mais confiável para tomar uma decisão.',
      servesFor:
        'Serve para reduzir tempo perdido procurando contexto, acelerar onboarding, diminuir dependência de pessoas-chave e dar base melhor para troubleshooting, changes e governança técnica.',
      insideCompany:
        'Dentro da empresa, ele funciona como uma camada operacional acima das ferramentas já existentes. Em vez de substituir wiki, SCM, cloud ou observabilidade, ele conecta esses pontos e entrega uma visão unificada por sistema, ambiente, serviço e owner.',
      curiosity:
        'A pergunta que ele responde muito bem é: se uma pessoa nova ou um time vizinho precisar atuar num sistema crítico hoje, quanto do caminho ela consegue percorrer sem depender de alguém específico?'
      ,
      outcomes: [
        'reduz tempo para entender sistemas e ambientes',
        'acelera onboarding técnico',
        'corta dependência de pessoas-chave',
        'dá contexto melhor para troubleshooting e mudança'
      ],
      pains: [
        'documentação espalhada entre wiki, tickets, repositórios e chat',
        'ownership pouco claro',
        'descoberta lenta de ambientes legados',
        'muito tempo gasto procurando histórico e dependências'
      ],
      audience: ['Platform Engineering', 'SRE', 'Infraestrutura', 'Operações', 'Enablement técnico'],
      capabilities: [
        {
          title: 'Ingestão e normalização',
          points: ['conectores para docs, SCM, cloud, K8s, tickets e observabilidade', 'entidades normalizadas por sistema, serviço e ambiente', 'score de frescor e confiabilidade por fonte']
        },
        {
          title: 'Busca e resposta com grounding',
          points: ['busca estruturada e semântica', 'respostas com citação obrigatória', 'tratamento explícito para baixa confiança']
        },
        {
          title: 'Mapa operacional',
          points: ['profiles por sistema e ambiente', 'ownership e dependências', 'mudanças, incidentes e runbooks relacionados']
        }
      ],
      workflow: [
        'ingestão das fontes e indexação',
        'resolução de entidades e relações',
        'navegação por profiles ou busca global',
        'uso do contexto em troubleshooting, onboarding e change review'
      ],
      companyUsage: [
        'times de suporte usam para descobrir rapidamente ownership, runbooks e histórico antes de escalar um incidente',
        'engenharia de plataforma usa para mapear serviços, ambientes e dependências sem reconstruir contexto manualmente',
        'novos membros usam como ponto inicial de onboarding para entender sistema, repositório, alertas e mudanças recentes'
      ],
      scenarios: [
        'um incidente abre e o operador precisa saber em minutos qual serviço está envolvido, quem é o owner e quais mudanças entraram por último',
        'um time precisa mexer num ambiente legado e não sabe onde estão as dependências, variáveis de contexto e conhecimento histórico',
        'a liderança quer reduzir o risco de conhecimento preso em poucas pessoas e ter uma visão mais viva do ambiente'
      ],
      integrations: ['GitHub/GitLab/Bitbucket', 'Confluence/Notion/Drive', 'Jira/ServiceNow', 'AWS/Azure/GCP', 'Kubernetes', 'Datadog/Grafana', 'Slack/Teams'],
      technology: ['Next.js + TypeScript', 'Node.js modular', 'PostgreSQL', 'OpenSearch ou pgvector', 'Temporal', 'OIDC/SAML', 'OpenTelemetry'],
      roadmap: [
        { phase: 'MVP', items: ['profiles de sistema e ambiente', 'ingestão de docs, SCM e cloud', 'busca com filtros e citações', 'score básico de frescor'] },
        { phase: 'V1', items: ['graph visual', 'contexto de incidentes e mudanças', 'packs de onboarding', 'mais conectores enterprise'] },
        { phase: 'V2', items: ['documentação sugerida por IA', 'detecção de gaps', 'recomendações guiadas por contexto'] }
      ],
      backlogThemes: ['connectors and ingestion', 'entity graph and search', 'context answer engine', 'knowledge governance and freshness']
    },
    {
      slug: 'runbook-ops',
      name: 'Dooloop Runbook Ops',
      kicker: 'Execução guiada e governada',
      headline: 'Padronize troubleshooting e operação repetitiva com runbooks versionados, evidência e passos seguros.',
      summary:
        'O Runbook Ops transforma procedimentos frágeis em fluxos versionados com steps manuais e automáticos, gates de aprovação, trilha de execução e suporte a incidentes.',
      description:
        'Ele pega aquilo que hoje vive em documentos soltos, scripts mal versionados ou memória dos operadores e transforma em execução guiada. O resultado é uma operação mais previsível, menos dependente de improviso e mais fácil de repassar para times diferentes.',
      servesFor:
        'Serve para padronizar troubleshooting, resposta a incidentes, manutenção recorrente, rotinas de operação e ações sensíveis que hoje dependem de pessoas muito específicas.',
      insideCompany:
        'Dentro da empresa, ele entra no dia a dia do NOC, suporte, SRE e times de operações cloud como um console de execução. Cada runbook deixa de ser só documentação e passa a ser fluxo operacional com validação, evidência e histórico.',
      curiosity:
        'A pergunta central aqui é: se seu melhor operador não estiver disponível, quanto da execução crítica o time ainda consegue realizar com segurança e consistência?'
      ,
      outcomes: [
        'reduz improviso operacional',
        'diminui escalonamento desnecessário',
        'aumenta repetibilidade e qualidade de execução',
        'gera trilha por step automaticamente'
      ],
      pains: [
        'troubleshooting dependente de operadores específicos',
        'scripts e passos sem governança',
        'runbooks desatualizados ou inexistentes',
        'prova fraca do que foi executado'
      ],
      audience: ['SRE', 'NOC', 'Suporte técnico', 'Operações cloud', 'Incident response'],
      capabilities: [
        {
          title: 'Authoring e governança',
          points: ['templates, versionamento e revisão', 'biblioteca de steps reutilizáveis', 'validade e review cadence']
        },
        {
          title: 'Execução guiada',
          points: ['steps manuais, decisões e automações', 'pause, resume e handoff', 'dry-run e rollback path']
        },
        {
          title: 'Troubleshooting assistido',
          points: ['runbooks por stack e sintoma', 'contexto de incidentes e mudanças', 'sugestões de próximo passo com IA']
        }
      ],
      workflow: [
        'autor cria e publica runbook com revisão',
        'operador dispara execução por incidente, sistema ou catálogo',
        'cada step coleta input, output e evidência',
        'resultado final alimenta histórico, analytics e auditoria'
      ],
      companyUsage: [
        'NOC e suporte usam para executar fluxos repetitivos e diagnósticos guiados sem depender de senioridade alta em todo atendimento',
        'SRE usa para incidentes, mitigação, rollback e verificações pós-change com mais disciplina operacional',
        'gestão usa os dados de execução para descobrir quais rotinas ainda são frágeis, lentas ou muito manuais'
      ],
      scenarios: [
        'um alerta dispara fora do horário comercial e o time precisa seguir uma sequência segura para mitigar impacto sem improviso',
        'um procedimento recorrente de manutenção exige checklist, evidência e controle de quem executou cada etapa',
        'a empresa quer reduzir escalonamentos desnecessários transformando conhecimento tácito em fluxo guiado'
      ],
      integrations: ['PagerDuty/Rootly', 'Slack/Teams', 'Jira/ServiceNow', 'SSH runners', 'GitHub Actions/GitLab CI/Jenkins', 'Kubernetes', 'cloud APIs'],
      technology: ['Next.js + TypeScript', 'Node.js', 'Temporal', 'PostgreSQL', 'runner adapters', 'vault-backed secrets', 'OpenTelemetry'],
      roadmap: [
        { phase: 'MVP', items: ['catálogo de runbooks', 'execução guiada', 'branching básico', 'evidência por step', 'uma integração de incidentes'] },
        { phase: 'V1', items: ['biblioteca maior de automações', 'entrada por incidente', 'handoff e analytics avançado', 'assistência de RCA'] },
        { phase: 'V2', items: ['simulation mode', 'quality scoring', 'runbook recommendation engine'] }
      ],
      backlogThemes: ['runbook authoring', 'execution orchestration', 'automation adapters', 'evidence capture', 'ai-assisted troubleshooting']
    },
    {
      slug: 'access-change-control',
      name: 'Dooloop Access & Change Control',
      kicker: 'Aprovação, política e execução controlada',
      headline: 'Governança real para acessos temporários, changes críticos e exceções operacionais.',
      summary:
        'O Access & Change Control substitui aprovações soltas por fluxos com política, janela, checklist, segregação de função, expiração e ligação direta com a execução autorizada.',
      description:
        'Ele organiza tudo o que normalmente vira ruído operacional: pedido de acesso temporário, mudança em produção, exceção de firewall, provisionamento sensível e paths de emergência. Em vez de depender de e-mail, chat e memória, o processo passa a ser claro, governado e auditável.',
      servesFor:
        'Serve para controlar quem pode pedir, aprovar e executar ações críticas, reduzindo risco operacional sem transformar a rotina em burocracia cega.',
      insideCompany:
        'Dentro da empresa, ele funciona como o front door de mudanças e acessos críticos. O solicitante abre o pedido com contexto, o motor aplica política e risco, os aprovadores decidem com base nas informações certas e a execução fica ligada ao request aprovado.',
      curiosity:
        'A pergunta forte aqui é: quantas ações críticas hoje entram em produção ou recebem acesso elevado sem uma trilha realmente confiável do começo ao fim?'
      ,
      outcomes: [
        'diminui risco de change e acesso privilegiado',
        'melhora velocidade de aprovação com contexto certo',
        'padroniza exceções e emergency paths',
        'deixa rastro completo para auditoria'
      ],
      pains: [
        'aprovação em chat ou e-mail',
        'acessos privilegiados sem expiração forte',
        'mudanças em produção sem trilha clara',
        'muita dependência de poucos aprovadores'
      ],
      audience: ['Infraestrutura', 'Segurança', 'Platform Ops', 'IT Governance', 'Change managers'],
      capabilities: [
        {
          title: 'Catálogo de solicitações',
          points: ['templates por tipo de request', 'campos de impacto, rollback e justificativa', 'escopo por sistema e ambiente']
        },
        {
          title: 'Orquestração de aprovação',
          points: ['routing por política e risco', 'janelas de manutenção', 'SLA, escalonamento e delegação']
        },
        {
          title: 'Controle e execução',
          points: ['expiração para acesso temporário', 'checklist obrigatório', 'break-glass com review posterior']
        }
      ],
      workflow: [
        'requester abre um template estruturado',
        'motor de política calcula gates e aprovadores',
        'workflow aplica checklists, janela e SoD',
        'execução aprovada fica ligada a evidência e encerramento'
      ],
      companyUsage: [
        'segurança e infraestrutura usam para acesso temporário e rastreável a ambientes críticos',
        'times de plataforma usam para governar changes, janelas e exceções operacionais',
        'gestores e auditores usam a mesma trilha para entender o que foi pedido, aprovado, executado e encerrado'
      ],
      scenarios: [
        'um engenheiro precisa de acesso privilegiado por poucas horas para atuar num incidente sem abrir espaço para acesso indefinido',
        'uma mudança crítica em produção exige janela, rollback, checklist e cadeia certa de aprovação',
        'uma exceção de emergência precisa acontecer rápido, mas sem perder revisão posterior e visibilidade'
      ],
      integrations: ['Okta/Azure AD/Google Workspace', 'Jira/ServiceNow', 'cloud IAM', 'Kubernetes RBAC', 'PAM/bastion', 'Slack/Teams'],
      technology: ['Next.js + TypeScript', 'Node.js', 'Temporal', 'PostgreSQL', 'policy service', 'OIDC/SAML', 'audit event bus'],
      roadmap: [
        { phase: 'MVP', items: ['flows de temporary access e production change', 'approval routing', 'checklist gating', 'audit history', 'expiração básica'] },
        { phase: 'V1', items: ['fulfillment real com IAM/PAM', 'change calendar', 'conflito de requests', 'emergency path refinado'] },
        { phase: 'V2', items: ['policy simulation', 'advanced risk scoring', 'mais tipos de exceção operacional'] }
      ],
      backlogThemes: ['request catalog', 'policy routing', 'approval inbox', 'access expiry lifecycle', 'controlled execution linkage']
    },
    {
      slug: 'evidence-ops',
      name: 'Dooloop Evidence Ops',
      kicker: 'Prova operacional sem planilha',
      headline: 'Converta aprovações, execuções, artefatos e sinais técnicos em evidência auditável com origem.',
      summary:
        'O Evidence Ops automatiza a coleta, organização e exportação de evidências técnicas ligadas a controles, auditorias e processos operacionais recorrentes.',
      description:
        'Ele transforma a rotina operacional em prova útil. Aprovações, execuções, logs, anexos, deploys e artefatos deixam de ficar espalhados e passam a compor um acervo de evidência com origem, vínculo e validade.',
      servesFor:
        'Serve para reduzir retrabalho em auditoria, melhorar prontidão para compliance, dar visibilidade para gaps de controle e reaproveitar evidência gerada pelos próprios fluxos da operação.',
      insideCompany:
        'Dentro da empresa, ele funciona como uma camada de prova sobre o que já aconteceu. Em vez de começar a auditoria perguntando quem tem screenshot ou planilha, o time já encontra evidências ligadas a controles, períodos, ambientes e owners.',
      curiosity:
        'A pergunta que ele expõe é simples: se uma auditoria começar amanhã, o quanto da evidência técnica relevante já está pronto e o quanto ainda depende de caça manual?'
      ,
      outcomes: [
        'reduz tempo de preparação para auditoria',
        'diminui coleta manual de screenshots',
        'melhora visibilidade de gaps de controle',
        'reaproveita prova gerada pela operação'
      ],
      pains: [
        'auditoria vira caça manual de evidência',
        'artefatos sem provenance clara',
        'controles sem owner ou validade visível',
        'muito retrabalho para montar pacotes de evidência'
      ],
      audience: ['Compliance', 'Security governance', 'Infra audit owners', 'CISO office'],
      capabilities: [
        {
          title: 'Coleta e vínculo',
          points: ['evidência gerada automaticamente por workflows', 'artefatos, logs e anexos com origem', 'ligação com requests, steps, deploys e aprovações']
        },
        {
          title: 'Mapeamento por controle',
          points: ['biblioteca de frameworks e controles', 'validade por evidência', 'reuso entre frameworks']
        },
        {
          title: 'Audit readiness',
          points: ['pacotes exportáveis', 'dashboards de gap e validade', 'attestation e sign-off']
        }
      ],
      workflow: [
        'evidências entram por automação ou coleta manual estruturada',
        'metadados e origem são registrados',
        'itens são mapeados a controles e períodos',
        'auditoria exporta bundles sem reconstruir manualmente'
      ],
      companyUsage: [
        'compliance usa para montar pacotes de auditoria sem reconstruir histórico em cima da hora',
        'times técnicos usam para provar execução, aprovação e mudança sem depender de documentação paralela',
        'liderança de segurança usa para enxergar controles sem evidência, vencimentos e exposição operacional'
      ],
      scenarios: [
        'um controle exige comprovação recorrente de change, aprovação e execução e hoje isso ainda é coletado em planilhas',
        'uma auditoria externa pede evidências de período fechado e o time precisa responder rápido com provenance',
        'a empresa quer transformar fluxos operacionais em prova reaproveitável para mais de um framework'
      ],
      integrations: ['todos os módulos Dooloop', 'cloud config/compliance', 'CI/CD', 'observability/logs', 'document repos', 'ticketing'],
      technology: ['PostgreSQL', 'object storage S3-compatible', 'hash and provenance registry', 'reporting service', 'Temporal', 'OpenTelemetry'],
      roadmap: [
        { phase: 'MVP', items: ['artifact registry', 'evidence linking', 'control mapping', 'audit bundle export', 'readiness dashboard'] },
        { phase: 'V1', items: ['mais frameworks', 'attestation flows', 'auditor view', 'questionnaire support'] },
        { phase: 'V2', items: ['continuous control scoring', 'advanced evidence graph', 'external trust sharing'] }
      ],
      backlogThemes: ['artifact registry', 'control library and mapping', 'readiness analytics', 'audit package export', 'attestation workflows']
    },
    {
      slug: 'delivery-governance',
      name: 'Dooloop Delivery Governance',
      kicker: 'Entrega técnica com rastreabilidade e controle',
      headline: 'Rastreie mudança, pipeline, deploy e uso de IA sem perder velocidade nem governança.',
      summary:
        'O Delivery Governance conecta issue, PR/MR, review, pipeline, deploy, waiver e assistência de IA em uma trilha única com gates por risco e evidência por release.',
      description:
        'Ele existe para um cenário cada vez mais comum: a empresa entrega rápido, usa automação em todo o fluxo e começa a sentir que revisar, rastrear e governar a mudança ficou mais difícil do que gerar a mudança em si. O produto organiza essa camada de controle sem voltar para um modelo pesado e lento.',
      servesFor:
        'Serve para dar visibilidade sobre o que foi pedido, alterado, revisado, aprovado, implantado e assistido por IA, além de criar gates proporcionais ao risco em vez de bloqueios genéricos.',
      insideCompany:
        'Dentro da empresa, ele atua entre plataforma, engenharia, DevOps e segurança como a trilha de governança da entrega. SCM, pipelines, deploys, approvals e exceções passam a conversar em um mesmo fluxo de mudança.',
      curiosity:
        'A pergunta que ele coloca na mesa é direta: sua empresa sabe realmente o que entrou em produção, por qual pipeline, com qual revisão e com qual grau de assistência por IA?'
      ,
      outcomes: [
        'melhora visibilidade de change lineage',
        'controla uso de IA em contexto de entrega',
        'reduz bottleneck de review com melhor contexto',
        'liga falha de pipeline a remediação governada'
      ],
      pains: [
        'ferramentas de SDLC desconectadas',
        'dificuldade para provar o que foi revisado e implantado',
        'governança fraca para código assistido por IA',
        'pipeline falhando sem caminho claro de remediação'
      ],
      audience: ['VP Engineering', 'Platform Engineering', 'DevOps leads', 'Secure SDLC owners', 'Release management'],
      capabilities: [
        {
          title: 'Lineage e provenance',
          points: ['issue → change → review → pipeline → deploy', 'metadata de origem do código', 'timeline de exceções e waivers']
        },
        {
          title: 'Policy gates',
          points: ['review por classe de risco', 'approvals por ambiente', 'release checklist e block reasons']
        },
        {
          title: 'Governança de IA e remediação',
          points: ['declaração de contribuição assistida por IA', 'registry de agentes e ações', 'triagem contextual para falhas de pipeline']
        }
      ],
      workflow: [
        'eventos de SCM, CI e deploy alimentam um change unit',
        'risco e regras de revisão são aplicados',
        'deploy só avança com gates satisfeitos',
        'falhas e exceções alimentam remediação e evidência'
      ],
      companyUsage: [
        'platform engineering usa para unificar a visão de lineage entre código, pipeline e deploy',
        'segurança usa para aplicar oversight mais forte a mudanças de maior risco e a contribuição assistida por IA',
        'engenharia usa para entender gargalos de revisão, bloqueios de release e falhas recorrentes de pipeline'
      ],
      scenarios: [
        'uma empresa começa a usar IA no desenvolvimento e precisa manter visibilidade e controle sobre o que chega em produção',
        'pipelines falham com frequência e ninguém consegue ligar claramente a falha ao contexto da mudança',
        'auditoria, incidente ou postmortem exigem reconstruir a história completa de uma release crítica'
      ],
      integrations: ['GitHub/GitLab/Bitbucket', 'GitHub Actions/GitLab CI/Jenkins', 'Argo CD', 'artifact registries', 'security scanners', 'Jira', 'incident and observability tools'],
      technology: ['event-driven ingestion', 'Temporal', 'PostgreSQL', 'policy service', 'AI governance layer', 'OpenTelemetry'],
      roadmap: [
        { phase: 'MVP', items: ['change lineage básico', 'review policy', 'pipeline/deploy traceability', 'AI-assisted change declaration', 'release evidence export'] },
        { phase: 'V1', items: ['scanner integration', 'contextual RCA', 'waiver controls', 'agent registry expandido'] },
        { phase: 'V2', items: ['policy simulation', 'governed remediation flows', 'portfolio analytics'] }
      ],
      backlogThemes: ['event ingestion and correlation', 'policy gates', 'ai governance registry', 'pipeline remediation flows', 'release evidence and reporting']
    }
  ],
  en: [
    {
      slug: 'context-hub',
      name: 'Dooloop Context Hub',
      kicker: 'Operational context discovery',
      headline: 'Find ownership, dependencies and real context without relying on memory or scattered search.',
      summary:
        'Context Hub consolidates docs, repositories, environments, tickets, incidents and technical metadata into a living context layer with grounded search, relationships and source-backed answers.',
      description:
        'In practice, it becomes the place platform, SRE, support and operations teams go when they need to quickly understand what exists in an environment, who owns each part, what changed recently, and which source is trustworthy enough to support a decision.',
      servesFor:
        'It is built to reduce time spent searching for context, accelerate onboarding, lower key-person dependency, and give better foundations for troubleshooting, change review and technical governance.',
      insideCompany:
        'Inside a company, it acts as an operational layer above the existing toolchain. Instead of replacing docs, SCM, cloud or observability, it connects them and serves a unified view by system, environment, service and owner.',
      curiosity:
        'The core question it answers well is: if a new person or adjacent team needs to act on a critical system today, how far can they go without depending on one specific individual?'
      ,
      outcomes: [
        'reduces time to understand systems and environments',
        'accelerates technical onboarding',
        'cuts dependency on key people',
        'improves context for troubleshooting and change review'
      ],
      pains: [
        'documentation spread across wiki, tickets, repositories and chat',
        'unclear ownership',
        'slow discovery of legacy environments',
        'too much time spent searching for history and dependencies'
      ],
      audience: ['Platform Engineering', 'SRE', 'Infrastructure', 'Operations', 'Technical enablement'],
      capabilities: [
        {
          title: 'Ingestion and normalization',
          points: ['connectors for docs, SCM, cloud, K8s, ticketing and observability', 'normalized entities by system, service and environment', 'freshness and trust scoring per source']
        },
        {
          title: 'Grounded search and answers',
          points: ['structured and semantic search', 'mandatory source citations', 'explicit low-confidence handling']
        },
        {
          title: 'Operational map',
          points: ['system and environment profiles', 'ownership and dependency mapping', 'related changes, incidents and runbooks']
        }
      ],
      workflow: [
        'ingest and index connected sources',
        'resolve entities and relationships',
        'navigate through profiles or global search',
        'use context in troubleshooting, onboarding and change review'
      ],
      companyUsage: [
        'support teams use it to discover ownership, runbooks and history before escalating an incident',
        'platform engineering uses it to map services, environments and dependencies without rebuilding context manually',
        'new team members use it as the starting point for onboarding into systems, repositories, alerts and recent changes'
      ],
      scenarios: [
        'an incident opens and the operator needs to know within minutes which service is involved, who owns it and what changed most recently',
        'a team needs to work on a legacy environment and lacks visibility into dependencies, context variables and historical knowledge',
        'leadership wants to reduce the risk of knowledge being trapped in a few people while improving live visibility into the estate'
      ],
      integrations: ['GitHub/GitLab/Bitbucket', 'Confluence/Notion/Drive', 'Jira/ServiceNow', 'AWS/Azure/GCP', 'Kubernetes', 'Datadog/Grafana', 'Slack/Teams'],
      technology: ['Next.js + TypeScript', 'Modular Node.js', 'PostgreSQL', 'OpenSearch or pgvector', 'Temporal', 'OIDC/SAML', 'OpenTelemetry'],
      roadmap: [
        { phase: 'MVP', items: ['system and environment profiles', 'docs, SCM and cloud ingestion', 'search with filters and citations', 'basic freshness scoring'] },
        { phase: 'V1', items: ['graph visual', 'incident and change context', 'onboarding packs', 'more enterprise connectors'] },
        { phase: 'V2', items: ['AI-assisted documentation drafting', 'gap detection', 'context-driven recommendations'] }
      ],
      backlogThemes: ['connectors and ingestion', 'entity graph and search', 'context answer engine', 'knowledge governance and freshness']
    },
    {
      slug: 'runbook-ops',
      name: 'Dooloop Runbook Ops',
      kicker: 'Guided and governed execution',
      headline: 'Standardize troubleshooting and repetitive operations with versioned runbooks, evidence and safe execution.',
      summary:
        'Runbook Ops turns fragile procedures into versioned flows with manual and automated steps, approval gates, execution traceability and incident-ready operation.',
      description:
        'It takes what currently lives in scattered documents, poorly governed scripts or operator memory and turns it into guided execution. The result is more predictable operations, less improvisation and a workflow that can be handed across teams without losing control.',
      servesFor:
        'It is built to standardize troubleshooting, incident response, recurring maintenance, operational routines and sensitive actions that today depend on very specific individuals.',
      insideCompany:
        'Inside the company, it becomes the execution console for NOC, support, SRE and cloud operations. Each runbook stops being just documentation and becomes an auditable operational flow with validation, evidence and history.',
      curiosity:
        'The key question here is: if your best operator is unavailable, how much of the critical execution path can the team still perform safely and consistently?'
      ,
      outcomes: [
        'reduces operational improvisation',
        'lowers unnecessary escalation',
        'improves repeatability and execution quality',
        'creates automatic step-level traceability'
      ],
      pains: [
        'troubleshooting depends on specific operators',
        'scripts and steps lack governance',
        'runbooks are stale or missing',
        'proof of execution is weak'
      ],
      audience: ['SRE', 'NOC', 'Technical support', 'Cloud operations', 'Incident response'],
      capabilities: [
        {
          title: 'Authoring and governance',
          points: ['templates, versioning and review', 'reusable step library', 'validity and review cadence']
        },
        {
          title: 'Guided execution',
          points: ['manual steps, decisions and automations', 'pause, resume and handoff', 'dry-run and rollback path']
        },
        {
          title: 'Assisted troubleshooting',
          points: ['stack- and symptom-based runbooks', 'incident and change context', 'AI-assisted next-step suggestions']
        }
      ],
      workflow: [
        'author creates and publishes a reviewed runbook',
        'operator launches execution from incident, system or catalog',
        'each step captures input, output and evidence',
        'final result feeds history, analytics and audit'
      ],
      companyUsage: [
        'NOC and support teams use it to run repetitive flows and guided diagnostics without requiring deep seniority in every call',
        'SRE uses it for incident mitigation, rollback and post-change verification with stronger operational discipline',
        'management uses execution data to identify fragile, slow or overly manual routines'
      ],
      scenarios: [
        'an alert fires after hours and the team needs a safe path to mitigate impact without improvising',
        'a recurring maintenance procedure requires checklist, evidence and clear attribution for every step',
        'the company wants to reduce unnecessary escalation by turning tacit knowledge into guided flow'
      ],
      integrations: ['PagerDuty/Rootly', 'Slack/Teams', 'Jira/ServiceNow', 'SSH runners', 'GitHub Actions/GitLab CI/Jenkins', 'Kubernetes', 'cloud APIs'],
      technology: ['Next.js + TypeScript', 'Node.js', 'Temporal', 'PostgreSQL', 'runner adapters', 'vault-backed secrets', 'OpenTelemetry'],
      roadmap: [
        { phase: 'MVP', items: ['runbook catalog', 'guided execution', 'basic branching', 'step-level evidence', 'one incident integration'] },
        { phase: 'V1', items: ['larger automation library', 'incident-triggered entry', 'handoff and advanced analytics', 'RCA assistance'] },
        { phase: 'V2', items: ['simulation mode', 'quality scoring', 'runbook recommendation engine'] }
      ],
      backlogThemes: ['runbook authoring', 'execution orchestration', 'automation adapters', 'evidence capture', 'ai-assisted troubleshooting']
    },
    {
      slug: 'access-change-control',
      name: 'Dooloop Access & Change Control',
      kicker: 'Approval, policy and controlled execution',
      headline: 'Real governance for temporary access, critical changes and operational exceptions.',
      summary:
        'Access & Change Control replaces loose approvals with workflows that enforce policy, maintenance windows, checklists, segregation of duties, expiration and direct linkage to approved execution.',
      description:
        'It organizes everything that usually turns into operational noise: temporary access, production change, firewall exception, sensitive provisioning and emergency paths. Instead of relying on email, chat and memory, the process becomes structured, governed and auditable.',
      servesFor:
        'It is meant to control who can request, approve and execute critical actions while reducing operational risk without forcing teams into blind bureaucracy.',
      insideCompany:
        'Inside the company, it acts as the front door for critical access and change. The requester opens the request with context, the policy engine applies risk and gates, approvers decide with the right information, and execution remains linked to the approved request.',
      curiosity:
        'The strong question it raises is: how many critical actions reach production or receive elevated access today without a truly reliable end-to-end trail?'
      ,
      outcomes: [
        'reduces risk around change and privileged access',
        'improves approval speed with the right context',
        'standardizes exception and emergency paths',
        'creates end-to-end audit traceability'
      ],
      pains: [
        'approvals happen in chat or email',
        'privileged access lacks strong expiration',
        'production changes have poor traceability',
        'too much dependency on a few approvers'
      ],
      audience: ['Infrastructure', 'Security', 'Platform Ops', 'IT Governance', 'Change managers'],
      capabilities: [
        {
          title: 'Request catalog',
          points: ['typed templates', 'impact, rollback and business justification fields', 'system and environment scoping']
        },
        {
          title: 'Approval orchestration',
          points: ['policy- and risk-based routing', 'maintenance windows', 'SLA, escalation and delegation']
        },
        {
          title: 'Control and execution',
          points: ['temporary access expiration', 'mandatory checklists', 'break-glass with post-facto review']
        }
      ],
      workflow: [
        'requester submits a structured request',
        'policy engine computes gates and approvers',
        'workflow enforces checklist, window and SoD',
        'approved execution remains linked to evidence and closure'
      ],
      companyUsage: [
        'security and infrastructure teams use it for temporary, traceable access to critical environments',
        'platform teams use it to govern production changes, windows and operational exceptions',
        'managers and auditors use the same trail to understand what was requested, approved, executed and closed'
      ],
      scenarios: [
        'an engineer needs privileged access for a few hours during an incident without creating indefinite exposure',
        'a critical production change requires a maintenance window, rollback plan, checklist and the right approval chain',
        'an emergency exception must happen quickly without losing retrospective review and visibility'
      ],
      integrations: ['Okta/Azure AD/Google Workspace', 'Jira/ServiceNow', 'cloud IAM', 'Kubernetes RBAC', 'PAM/bastion', 'Slack/Teams'],
      technology: ['Next.js + TypeScript', 'Node.js', 'Temporal', 'PostgreSQL', 'policy service', 'OIDC/SAML', 'audit event bus'],
      roadmap: [
        { phase: 'MVP', items: ['temporary access and production change flows', 'approval routing', 'checklist gating', 'audit history', 'basic expiry'] },
        { phase: 'V1', items: ['real IAM/PAM fulfillment', 'change calendar', 'request conflict detection', 'refined emergency path'] },
        { phase: 'V2', items: ['policy simulation', 'advanced risk scoring', 'broader operational exception types'] }
      ],
      backlogThemes: ['request catalog', 'policy routing', 'approval inbox', 'access expiry lifecycle', 'controlled execution linkage']
    },
    {
      slug: 'evidence-ops',
      name: 'Dooloop Evidence Ops',
      kicker: 'Operational proof without spreadsheets',
      headline: 'Turn approvals, executions, artifacts and technical signals into auditable evidence with provenance.',
      summary:
        'Evidence Ops automates the collection, organization and export of technical evidence tied to controls, audits and recurring operational processes.',
      description:
        'It turns operational routine into useful proof. Approvals, executions, logs, attachments, deployments and artifacts stop being scattered across tools and start composing a body of evidence with provenance, linkage and validity.',
      servesFor:
        'It is built to reduce audit rework, improve compliance readiness, expose control gaps and reuse the proof already generated by day-to-day operations.',
      insideCompany:
        'Inside the company, it acts as a proof layer on top of what has already happened. Instead of starting an audit by asking who has screenshots or spreadsheets, teams find evidence already linked to controls, time periods, environments and owners.',
      curiosity:
        'The question it surfaces is simple: if an audit starts tomorrow, how much of the relevant technical evidence is already ready and how much still depends on manual hunting?'
      ,
      outcomes: [
        'reduces audit preparation time',
        'cuts manual screenshot collection',
        'improves visibility into control gaps',
        'reuses proof generated by operations'
      ],
      pains: [
        'audits become manual hunts for evidence',
        'artifacts lack clear provenance',
        'controls lack visible ownership or validity windows',
        'too much rework to assemble evidence packages'
      ],
      audience: ['Compliance', 'Security governance', 'Infrastructure audit owners', 'CISO office'],
      capabilities: [
        {
          title: 'Collection and linkage',
          points: ['workflow-generated evidence', 'artifacts, logs and attachments with origin', 'linkage across requests, steps, deployments and approvals']
        },
        {
          title: 'Control mapping',
          points: ['framework and control library', 'evidence validity windows', 'cross-framework reuse']
        },
        {
          title: 'Audit readiness',
          points: ['exportable bundles', 'gap and validity dashboards', 'attestation and sign-off']
        }
      ],
      workflow: [
        'evidence enters via automation or structured manual capture',
        'metadata and provenance are recorded',
        'items are mapped to controls and periods',
        'audits export bundles without manual reconstruction'
      ],
      companyUsage: [
        'compliance uses it to assemble audit packages without reconstructing history at the last minute',
        'technical teams use it to prove execution, approval and change without parallel documentation effort',
        'security leadership uses it to spot controls with missing evidence, expiring validity and operational exposure'
      ],
      scenarios: [
        'a control requires recurring proof of change, approval and execution and today the team still collects it in spreadsheets',
        'an external audit asks for a closed period and the team must answer quickly with provenance-rich evidence',
        'the company wants to turn operational flows into reusable proof across more than one framework'
      ],
      integrations: ['all Dooloop modules', 'cloud config/compliance', 'CI/CD', 'observability/logging', 'document repositories', 'ticketing'],
      technology: ['PostgreSQL', 'S3-compatible object storage', 'hash and provenance registry', 'reporting service', 'Temporal', 'OpenTelemetry'],
      roadmap: [
        { phase: 'MVP', items: ['artifact registry', 'evidence linking', 'control mapping', 'audit bundle export', 'readiness dashboard'] },
        { phase: 'V1', items: ['more frameworks', 'attestation flows', 'auditor view', 'questionnaire support'] },
        { phase: 'V2', items: ['continuous control scoring', 'advanced evidence graph', 'external trust sharing'] }
      ],
      backlogThemes: ['artifact registry', 'control library and mapping', 'readiness analytics', 'audit package export', 'attestation workflows']
    },
    {
      slug: 'delivery-governance',
      name: 'Dooloop Delivery Governance',
      kicker: 'Technical delivery with traceability and control',
      headline: 'Track change, pipeline, deployment and AI usage without losing speed or governance.',
      summary:
        'Delivery Governance connects issue, PR/MR, review, pipeline, deployment, waiver and AI assistance into one traceable flow with risk-based gates and release evidence.',
      description:
        'It is built for a reality that is becoming common: companies ship faster, automation is everywhere, and reviewing, tracing and governing change is becoming harder than generating the change itself. The product organizes that control layer without pushing teams back into a slow, heavy process.',
      servesFor:
        'It is meant to expose what was requested, changed, reviewed, approved, deployed and AI-assisted, while creating risk-proportional gates instead of generic blockers.',
      insideCompany:
        'Inside the company, it sits between platform, engineering, DevOps and security as the governance trail of delivery. SCM, pipelines, deployments, approvals and exceptions become part of one change flow.',
      curiosity:
        'The direct question it puts on the table is: does your company really know what reached production, through which pipeline, under which review, and with what degree of AI assistance?'
      ,
      outcomes: [
        'improves visibility into change lineage',
        'governs AI usage in the delivery path',
        'reduces review bottlenecks through better context',
        'links pipeline failure to governed remediation'
      ],
      pains: [
        'disconnected SDLC tooling',
        'hard to prove what was reviewed and deployed',
        'weak governance for AI-assisted code',
        'pipeline failures lack a clear remediation path'
      ],
      audience: ['VP Engineering', 'Platform Engineering', 'DevOps leads', 'Secure SDLC owners', 'Release management'],
      capabilities: [
        {
          title: 'Lineage and provenance',
          points: ['issue → change → review → pipeline → deployment', 'code origin metadata', 'waiver and exception timeline']
        },
        {
          title: 'Policy gates',
          points: ['review by risk class', 'environment-based approvals', 'release checklist and block reasons']
        },
        {
          title: 'AI governance and remediation',
          points: ['AI-assisted contribution declaration', 'agent and action registry', 'contextual triage for pipeline failures']
        }
      ],
      workflow: [
        'SCM, CI and deployment events feed a governed change unit',
        'risk and review rules are applied',
        'deployment proceeds only when gates are met',
        'failures and exceptions feed remediation and evidence'
      ],
      companyUsage: [
        'platform engineering uses it to unify lineage across code, pipeline and deployment',
        'security uses it to apply stronger oversight to higher-risk changes and AI-assisted contribution',
        'engineering leadership uses it to understand review bottlenecks, release blockers and recurring pipeline failure patterns'
      ],
      scenarios: [
        'a company starts adopting AI in software delivery and needs better visibility and control over what reaches production',
        'pipelines fail often and nobody can clearly connect the failure to the surrounding change context',
        'an audit, incident or postmortem requires reconstructing the full story of a critical release'
      ],
      integrations: ['GitHub/GitLab/Bitbucket', 'GitHub Actions/GitLab CI/Jenkins', 'Argo CD', 'artifact registries', 'security scanners', 'Jira', 'incident and observability tools'],
      technology: ['event-driven ingestion', 'Temporal', 'PostgreSQL', 'policy service', 'AI governance layer', 'OpenTelemetry'],
      roadmap: [
        { phase: 'MVP', items: ['basic change lineage', 'review policy', 'pipeline/deployment traceability', 'AI-assisted change declaration', 'release evidence export'] },
        { phase: 'V1', items: ['scanner integration', 'contextual RCA', 'waiver controls', 'expanded agent registry'] },
        { phase: 'V2', items: ['policy simulation', 'governed remediation flows', 'portfolio analytics'] }
      ],
      backlogThemes: ['event ingestion and correlation', 'policy gates', 'ai governance registry', 'pipeline remediation flows', 'release evidence and reporting']
    }
  ]
};
